import React, { memo } from 'react';

import { Box, Typography } from '@mui/material';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import { LabelSize, LabelVariant } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { ValueStub } from 'constants/data';
import { COMMON_DATETIME_FORMAT } from 'constants/date';
import {
  TaskHistoryAudience,
  TaskHistoryAudienceLabel,
  TaskHistoryEntryKind,
  TaskStatusLabel,
} from 'constants/tasks';

import {
  AUDIENCE_LABEL_VARIANT,
  RETURN_ENTRY_TITLE,
  TaskThreadTagType,
} from '../../TaskThread.constants';
import {
  entryBodyStyles,
  entryHeaderStyles,
  entryMetaStyles,
  entryStyles,
} from '../../TaskThread.styles';
import { TaskThreadTestIds } from '../../TaskThread.testIds';

const TaskThreadEntry = ({
  taskHistoryRecordId,
  entryKind,
  audience = TaskHistoryAudience.INTERNAL_ONLY,
  eventDate,
  userName,
  message,
  reason,
  status,
  tagType = TaskThreadTagType.AUDIENCE,
}) => {
  const isReturn = entryKind === TaskHistoryEntryKind.RETURN;
  const body = message || reason;
  const isStatusTag = tagType === TaskThreadTagType.STATUS;
  const hasStatusTag = !!status && (isReturn || entryKind === TaskHistoryEntryKind.STATUS_CHANGE);

  return (
    <Box data-testid={TaskThreadTestIds.getEntryId(taskHistoryRecordId)} sx={entryStyles}>
      <Box sx={entryHeaderStyles}>
        <Box sx={entryMetaStyles}>
          <Typography variant="medium14">{userName || 'System'}</Typography>
          {isStatusTag ? (
            hasStatusTag && (
              <LabelCDS
                size={LabelSize.S}
                title={TaskStatusLabel[status] ?? status}
                variant={LabelVariant.GRAY}
              />
            )
          ) : (
            <Box
              component="span"
              data-testid={TaskThreadTestIds.getEntryAudienceTagId(taskHistoryRecordId)}
            >
              <LabelCDS
                size={LabelSize.S}
                title={
                  TaskHistoryAudienceLabel[audience] ??
                  TaskHistoryAudienceLabel[TaskHistoryAudience.INTERNAL_ONLY]
                }
                variant={
                  AUDIENCE_LABEL_VARIANT[audience] ??
                  AUDIENCE_LABEL_VARIANT[TaskHistoryAudience.INTERNAL_ONLY]
                }
              />
            </Box>
          )}
        </Box>
        <Typography color="text.secondary" variant="regular12">
          {eventDate ? dayjs(eventDate).format(COMMON_DATETIME_FORMAT) : ValueStub.COMMON}
        </Typography>
      </Box>

      {isReturn && (
        <Typography color="error" variant="medium14">
          {RETURN_ENTRY_TITLE}
        </Typography>
      )}

      {body && (
        <Typography color="text.dark" sx={entryBodyStyles} variant="regular14">
          {body}
        </Typography>
      )}
    </Box>
  );
};

TaskThreadEntry.propTypes = {
  taskHistoryRecordId: PropTypes.string,
  entryKind: PropTypes.string,
  audience: PropTypes.string,
  eventDate: PropTypes.string,
  userName: PropTypes.string,
  message: PropTypes.string,
  reason: PropTypes.string,
  status: PropTypes.string,
  tagType: PropTypes.oneOf(Object.values(TaskThreadTagType)),
};

export default memo(TaskThreadEntry);
